import { Button, Drawer, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { Icons, InformationModal, TextInputFieldProps } from 'utoniq-ui'

import { formatPhoneNumber } from '@/clients/utoniq-core'
import {
  BuyNowSMSVerification,
  BuyNowSMSVerificationProps,
} from '@/components/pages/ArtistStoreItemDetail/template/components'
import { useMessage, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

import { TextInputCell } from './Common'

type PhoneNumberInputCellProps = Omit<BuyNowSMSVerificationProps, 'onClose'> & {
  formattedPhoneNumber: string
  isPhoneNumberVerified: boolean
  isRequired?: boolean
  sendingSMSCode: boolean
  needVerifyPhoneNumber?: boolean
  inputProps: Omit<TextInputFieldProps, 't' | 'type'>
  onChangeNeedVerifyPhoneNumber: (needVerifyPhoneNumber: boolean) => void
  onSendVerificationSMS: () => Promise<void>
}

export const PhoneNumberInputCell: Cell<PhoneNumberInputCellProps> = ({
  formattedPhoneNumber,
  isPhoneNumberVerified,
  needVerifyPhoneNumber,
  isRequired,
  inputProps,
  onChangeNeedVerifyPhoneNumber,
  ...buyNowSMSVerificationProps
}) => {
  const [openSMSVerificationModal, setOpenSMSVerificationModal] =
    useState<boolean>(false)

  useEffect(() => {
    setOpenSMSVerificationModal(!!needVerifyPhoneNumber)
  }, [needVerifyPhoneNumber])

  return (
    <CellFrame sx={{ pt: 6 }}>
      {isPhoneNumberVerified ? (
        <Stack spacing={2} pb={6}>
          <LabelCell
            isRequired={isRequired}
            isPhoneNumberVerified={isPhoneNumberVerified}
          />
          <Typography variant="body2">{formattedPhoneNumber}</Typography>
        </Stack>
      ) : (
        <TextInputCell
          type="number"
          {...inputProps}
          titleProps={{
            children: (
              <LabelCell
                isRequired={isRequired}
                isPhoneNumberVerified={isPhoneNumberVerified}
              />
            ),
          }}
        />
      )}
      <SMSVerificationDrawer
        {...buyNowSMSVerificationProps}
        open={openSMSVerificationModal}
        onClose={() => {
          setOpenSMSVerificationModal(false)
          onChangeNeedVerifyPhoneNumber(false)
        }}
        formattedPhoneNumber={formatPhoneNumber(
          buyNowSMSVerificationProps.phoneNumber
        )}
      />
    </CellFrame>
  )
}

type LabelCellProps = {
  isRequired: PhoneNumberInputCellProps['isRequired']
  isPhoneNumberVerified: PhoneNumberInputCellProps['isPhoneNumberVerified']
}
const LabelCell: Cell<LabelCellProps> = ({
  isRequired,
  isPhoneNumberVerified,
}) => {
  const { t } = useTranslation('personalInfoSettings')
  return (
    <CellFrame>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle1" color="text.secondary">
            {t('phoneNumber', '電話番号')}
          </Typography>
          {isRequired && (
            <Typography
              variant="caption"
              color="error.main"
              component="p"
              ml={1}
              mt={-1}
            >
              {t('required', '※必須')}
            </Typography>
          )}
        </Stack>
        {isPhoneNumberVerified ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Icons.Success fontSize="small" sx={{ color: 'grey.700' }} />
            <Typography variant="body2" color="text.hint">
              {t('verified')}
            </Typography>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center">
            <Icons.Info fontSize="small" sx={{ color: 'error.main' }} />
            <Typography variant="body2" color="error">
              {t('unverify', '未認証')}
            </Typography>
          </Stack>
        )}
      </Stack>
    </CellFrame>
  )
}

type SMSVerificationDrawerProps = Omit<
  BuyNowSMSVerificationProps,
  'phoneNumber'
> & {
  open: boolean
  sendingSMSCode: boolean
  formattedPhoneNumber: string
  onClose: () => void
  onSendVerificationSMS: () => Promise<void>
}
export const SMSVerificationDrawer: Cell<SMSVerificationDrawerProps> = ({
  open,
  sendingSMSCode,
  onClose,
  formattedPhoneNumber,
  onSendVerificationSMS,
  ...props
}) => {
  const { t } = useTranslation('personalInfoSettings')
  const { showError } = useMessage()
  const [openEnterSMSVerification, setOpenEnterSMSVerification] =
    useState<boolean>(false)

  return (
    <>
      <Drawer
        open={openEnterSMSVerification}
        onClose={() => setOpenEnterSMSVerification(false)}
        sx={{ zIndex: theme => theme.zIndex.modal + 3 }}
      >
        <Stack sx={{ bgcolor: 'background.default', height: '90dvh' }}>
          <BuyNowSMSVerification
            {...props}
            onVerificationPhoneNumber={value =>
              props
                .onVerificationPhoneNumber(value)
                .then(() => setOpenEnterSMSVerification(false))
            }
            phoneNumber={formattedPhoneNumber}
            onClose={() => setOpenEnterSMSVerification(false)}
          />
        </Stack>
      </Drawer>
      <InformationModal
        open={open}
        onClose={onClose}
        title={t('verifyTitle')}
        content={
          <Typography variant="body2" align="left">
            {t('verifyDesc2', { phoneNumber: formattedPhoneNumber })}
          </Typography>
        }
        buttons={
          <Button
            sx={{ minWidth: 212, mt: 4 }}
            size="large"
            loading={sendingSMSCode}
            disabled={sendingSMSCode}
            onClick={() => {
              onSendVerificationSMS()
                .then(() => {
                  onClose()
                  setOpenEnterSMSVerification(true)
                })
                .catch(showError)
            }}
          >
            {t('verify')}
          </Button>
        }
        cancelText={t('close')}
      />
    </>
  )
}
